import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp, doc, getDoc } from "firebase/firestore";
import { useApp } from "../App";
import { IconCamera } from "../components/Icons";

const CLOUDINARY_CLOUD = "dhjnbxsdk";
const CLOUDINARY_PRESET = "kepti_listings";

const CATEGORIES = [
  { key: "furnitureHome", label: "Furniture & Home" },
  { key: "bikesSports", label: "Bikes & Sports" },
  { key: "kidsClothing", label: "Kids' Clothing & Shoes" },
  { key: "electronics", label: "Electronics" },
  { key: "booksToys", label: "Books & Toys" },
  { key: "babyToddler", label: "Baby & Toddler" },
  { key: "other", label: "Other" },
];

const CONDITIONS = [
  { key: "likeNew", label: "Like new" },
  { key: "good", label: "Good condition" },
  { key: "used", label: "Well used" },
];

const PAYMENTS = [
  { key: "mbway", label: "MBWay" },
  { key: "cash", label: "Cash" },
  { key: "both", label: "MBWay or cash" },
];

const LOCATIONS = [
  { group: "Cascais line", options: [
    "Cascais",
    "Estoril",
    "Birre / Areia / Tires",
    "São Domingos de Rana",
    "Alcabideche",
    "Parede",
    "Carcavelos",
    "São João do Estoril",
  ]},
  { group: "Oeiras", options: [
    "Oeiras",
    "Miraflores",
    "Algés",
    "Porto Salvo",
    "Barcarena",
  ]},
  { group: "Lisbon", options: [
    "Belém / Restelo",
    "Príncipe Real / Estrela",
    "Campo de Ourique / Lapa",
    "Chiado / Bairro Alto",
    "Avenidas Novas",
    "Parque das Nações",
  ]},
  { group: "Sintra / North", options: [
    "Sintra",
    "Queluz",
    "Amadora",
  ]},
  { group: "Other", options: [
    "Other side of river",
    "Outside Lisbon",
  ]},
];

const SIZES = [
  { group: "By age", options: [
    "0–3 months",
    "3–6 months",
    "6–9 months",
    "9–12 months",
    "12–18 months",
    "18–24 months",
    "2–3 years",
    "3–4 years",
    "5–6 years",
    "7–8 years",
    "9–10 years",
    "11–12 years",
    "13–14 years",
  ]},
  { group: "By EU clothing size", options: [
    "44 cm",
    "50 cm",
    "56 cm",
    "62 cm",
    "68 cm",
    "74 cm",
    "80 cm",
    "86 cm",
    "92 cm",
    "98 cm",
    "104 cm",
    "110 cm",
    "116 cm",
    "122 cm",
    "128 cm",
    "134 cm",
    "140 cm",
    "146 cm",
    "152 cm",
    "158 cm",
    "164 cm",
  ]},
  { group: "By EU shoe size", options: [
    "EU 16", "EU 17", "EU 18", "EU 19", "EU 20",
    "EU 21", "EU 22", "EU 23", "EU 24", "EU 25",
    "EU 26", "EU 27", "EU 28", "EU 29", "EU 30",
    "EU 31", "EU 32", "EU 33", "EU 34", "EU 35",
    "EU 36", "EU 37", "EU 38", "EU 39",
  ]},
];

export default function PostListing({ onDone }) {
  const { user, t } = useApp();
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [price, setPrice] = useState("");
  const [condition, setCondition] = useState("");
  const [payment, setPayment] = useState("");
  const [location, setLocation] = useState("");
  const [size, setSize] = useState("");
  const [note, setNote] = useState("");
  const [movingSale, setMovingSale] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [whatsapp, setWhatsapp] = useState("");

  useEffect(() => {
    const fetchWhatsapp = async () => {
      if (!user) return;
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists()) {
        setWhatsapp(snap.data().whatsapp || "");
      }
    };
    fetchWhatsapp();
  }, [user]);

  useEffect(() => {
    if (category !== "kidsClothing") setSize("");
  }, [category]);

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", CLOUDINARY_PRESET);
    const res = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/image/upload`,
      { method: "POST", body: formData }
    );
    const data = await res.json();
    return data.secure_url;
  };

  const handleSubmit = async () => {
    setError("");
    if (!whatsapp) return setError(t.whatsappRequired);
    if (!photo) return setError(t.photoRequired);
    if (!title.trim()) return setError(t.titleRequired);
    if (!category) return setError(t.categoryRequired);
    if (price === "") return setError(t.priceRequired);
    if (!condition) return setError(t.conditionRequired);
    if (!payment) return setError(t.paymentRequired);
    if (!location) return setError(t.locationRequired);

    setSubmitting(true);
    try {
      const imageUrl = await uploadToCloudinary(photo);
      const listing = {
        title: title.trim(),
        category,
        price: parseFloat(price) || 0,
        condition,
        payment,
        location,
        note: note.trim(),
        movingSale,
        imageUrl,
        status: "active",
        userId: user.uid,
        userName: user.displayName || "",
        userWhatsApp: whatsapp,
        createdAt: serverTimestamp(),
      };
      if (category === "kidsClothing" && size) {
        listing.size = size;
      }
      await addDoc(collection(db, "market_listings"), listing);
      onDone();
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="post-page">
      <div className="post-header">
        <h1>{t.sellSomething}</h1>
      </div>
      <div className="post-body">

        {/* Photo */}
        <label className="photo-upload">
          {photoPreview && <img src={photoPreview} alt="preview" />}
          {!photoPreview && (
            <>
              <IconCamera />
              <span className="photo-upload-label">{t.addPhoto}</span>
            </>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={handlePhoto}
            style={{ display: "none" }}
          />
        </label>

        {/* Title */}
        <div className="form-group">
          <label className="form-label">{t.title}</label>
          <input
            className="form-input"
            type="text"
            placeholder={t.titlePlaceholder}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        {/* Category */}
        <div className="form-group">
          <label className="form-label">{t.category}</label>
          <select
            className="form-select"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">{t.selectCategory}</option>
            {CATEGORIES.map((c) => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
        </div>

        {/* Size — only for Kids' Clothing & Shoes */}
        {category === "kidsClothing" && (
          <div className="form-group">
            <label className="form-label">{t.size}</label>
            <select
              className="form-select"
              value={size}
              onChange={(e) => setSize(e.target.value)}
            >
              <option value="">Select size</option>
              {SIZES.map((group) => (
                <optgroup key={group.group} label={group.group}>
                  {group.options.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        )}

        {/* Price */}
        <div className="form-group">
          <label className="form-label">{t.price}</label>
          <input
            className="form-input"
            type="number"
            inputMode="decimal"
            placeholder={t.pricePlaceholder}
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            min="0"
          />
        </div>

        {/* Condition */}
        <div className="form-group">
          <label className="form-label">{t.condition}</label>
          <select
            className="form-select"
            value={condition}
            onChange={(e) => setCondition(e.target.value)}
          >
            <option value="">{t.selectCondition}</option>
            {CONDITIONS.map((c) => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
        </div>

        {/* Payment */}
        <div className="form-group">
          <label className="form-label">{t.payment}</label>
          <select
            className="form-select"
            value={payment}
            onChange={(e) => setPayment(e.target.value)}
          >
            <option value="">{t.selectPayment}</option>
            {PAYMENTS.map((p) => (
              <option key={p.key} value={p.key}>{p.label}</option>
            ))}
          </select>
        </div>

        {/* Location */}
        <div className="form-group">
          <label className="form-label">{t.location}</label>
          <select
            className="form-select"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          >
            <option value="">{t.selectLocation}</option>
            {LOCATIONS.map((group) => (
              <optgroup key={group.group} label={group.group}>
                {group.options.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        {/* Description */}
        <div className="form-group">
          <label className="form-label">{t.description}</label>
          <textarea
            className="form-textarea"
            placeholder={t.descriptionPlaceholder}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        {/* Moving sale */}
        <label className="form-checkbox-row">
          <input
            type="checkbox"
            checked={movingSale}
            onChange={(e) => setMovingSale(e.target.checked)}
          />
          <span className="form-checkbox-label">{t.movingSaleCheck}</span>
        </label>

        {/* Error */}
        {error && <div className="error-msg">{error}</div>}

        {/* Submit */}
        <button
          className="submit-btn"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? t.posting : t.postListing}
        </button>

      </div>
    </div>
  );
}
